(() => {
  const ROOT_SELECTOR =
    '.fw-quote[data-fw-quote-rail-page]';

  const TRACK_SELECTOR =
    '[data-fw-quote-rail-track]';

  const RAIL_SELECTOR =
    '[data-fw-quote-rail]';

  const FORM_SELECTOR =
    '.fw-quote-form';

  const HEADER_SELECTOR =
    '.fw-header';

  const VIEWPORT_CLASS =
    'fw-quote__rail-viewport';

  const desktopQuery =
    window.matchMedia(
      '(min-width: 991px)'
    );

  const HEADER_GAP = 24;
  const VIEWPORT_BOTTOM_GAP = 24;

  const clamp = (
    value,
    min,
    max
  ) => {
    return Math.min(
      Math.max(
        value,
        min
      ),
      max
    );
  };

  const getScrollY = () => {
    return (
      window.scrollY ||
      document.documentElement.scrollTop ||
      0
    );
  };

  const initRoot = (root) => {
    if (
      !root ||
      root.dataset.fwQuoteRailReady ===
        'true'
    ) {
      return;
    }

    const track =
      root.querySelector(
        TRACK_SELECTOR
      );

    const rail =
      root.querySelector(
        RAIL_SELECTOR
      );

    const form =
      root.querySelector(
        FORM_SELECTOR
      );

    const header =
      document.querySelector(
        HEADER_SELECTOR
      );

    if (
      !track ||
      !rail
    ) {
      return;
    }

    const viewport =
      document.createElement(
        'div'
      );

    viewport.className =
      VIEWPORT_CLASS;

    viewport.setAttribute(
      'data-fw-quote-rail-viewport',
      ''
    );

    rail.before(
      viewport
    );

    viewport.appendChild(
      rail
    );

    root.dataset.fwQuoteRailReady =
      'true';

    let frame = null;

    const isDesktop = () => {
      return desktopQuery.matches;
    };

    const clearGeometry = () => {
      root.style.removeProperty(
        '--fw-quote-rail-sticky-top'
      );

      root.style.removeProperty(
        '--fw-quote-rail-viewport-height'
      );

      root.style.removeProperty(
        '--fw-quote-rail-left'
      );

      root.style.removeProperty(
        '--fw-quote-rail-width'
      );

      root.style.removeProperty(
        '--fw-quote-rail-offset'
      );

      track.style.removeProperty(
        'min-height'
      );

      viewport.style.removeProperty(
        'overflow'
      );

      viewport.classList.remove(
        'is-virtual',
        'is-active'
      );
    };

    const render = () => {
      frame = null;

      if (!isDesktop()) {
        clearGeometry();
        return;
      }

      const scrollY =
        getScrollY();

      const headerBottom =
        header
          ? Math.max(
              0,
              header
                .getBoundingClientRect()
                .bottom
            )
          : 0;

      const stickyTop =
        headerBottom +
        HEADER_GAP;

      const availableHeight =
        Math.max(
          160,
          window.innerHeight -
            stickyTop -
            VIEWPORT_BOTTOM_GAP
        );

      const railHeight =
        rail.scrollHeight;

      const viewportHeight =
        Math.min(
          railHeight,
          availableHeight
        );

      const maxOffset =
        Math.max(
          0,
          railHeight -
            viewportHeight
        );

      root.style.setProperty(
        '--fw-quote-rail-sticky-top',
        `${stickyTop.toFixed(2)}px`
      );

      root.style.setProperty(
        '--fw-quote-rail-viewport-height',
        `${viewportHeight.toFixed(2)}px`
      );

      /*
       * Reserve at least the complete natural rail height.
       *
       * If the form is taller than the rail, the grid will stretch
       * this track farther automatically.
       */
      track.style.minHeight =
        `${Math.ceil(railHeight)}px`;

      /*
       * Hide the portion of a long rail that has not yet travelled
       * into the sticky viewport.
       */
      viewport.style.overflow =
        'hidden';

      /*
       * Never promote the rail viewport to position: fixed.
       * Sticky positioning keeps it constrained by this track and
       * therefore by the RFQ section itself.
       */
      viewport.classList.remove(
        'is-virtual',
        'is-active'
      );

      const trackRect =
        track.getBoundingClientRect();

      const trackDocumentTop =
        scrollY +
        trackRect.top;

      const stickyStart =
        Math.max(
          0,
          trackDocumentTop -
            stickyTop
        );

      const stickyTravel =
        Math.max(
          0,
          trackRect.height -
            viewportHeight
        );

      let progress = 0;

      if (
        maxOffset > 0 &&
        stickyTravel > 0
      ) {
        progress =
          clamp(
            (
              scrollY -
              stickyStart
            ) /
              stickyTravel,
            0,
            1
          );
      }

      const offset =
        maxOffset *
        progress;

      root.style.setProperty(
        '--fw-quote-rail-offset',
        `${offset.toFixed(2)}px`
      );
    };

    const scheduleRender = () => {
      if (
        frame !== null
      ) {
        return;
      }

      frame =
        window.requestAnimationFrame(
          render
        );
    };

    const handleResize = () => {
      scheduleRender();
    };

    window.addEventListener(
      'scroll',
      scheduleRender,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      handleResize
    );

    desktopQuery.addEventListener?.(
      'change',
      handleResize
    );

    if (
      'ResizeObserver' in
      window
    ) {
      const resizeObserver =
        new ResizeObserver(
          scheduleRender
        );

      resizeObserver.observe(
        rail
      );

      resizeObserver.observe(
        track
      );

      if (form) {
        resizeObserver.observe(
          form
        );
      }

      if (header) {
        resizeObserver.observe(
          header
        );
      }
    }

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(
        scheduleRender
      );
    }

    window.addEventListener(
      'load',
      scheduleRender,
      {
        once: true
      }
    );

    scheduleRender();
  };

  const initWithin = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initRoot(
        scope
      );
    }

    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(
        initRoot
      );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initWithin();
      },
      {
        once: true
      }
    );
  } else {
    initWithin();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(
        event.target
      );
    }
  );
})();